import useNotificationApi from './notifications'
import usePurchaseApi from './purchases-info'
import type {
	AddHealthCustodianPayload,
	HealthCustodianResponse,
	IRequestResponse,
	PaginationContext,
	AddTravelCustodianPayload,
	CustodianTravelPurchaseResponse
} from '~/types'

export default function useProductActivationApi() {
	const { getPurchaseDetail: getPurchase } = usePurchaseApi()
	const getCustodianAutoPurchase = async (purchase_id: string) => {
		return await getPurchase<CustodianAutoPurchaseResponse>(`${purchase_id}/auto-plans`)
	}
	const getCustodianGadgetPurchase = async (purchase_id: string) => {
		return await getPurchase<GadgetCustodianPurchaseResponse>(`${purchase_id}/gadget-plans`)
	}
	const getTravelCustodianPurchase = async (purchase_id: string) => {
		return await getPurchase<CustodianTravelPurchaseResponse>(`${purchase_id}/travel-plans`)
	}
	const addAutoCustodian = async (payload: AddAutoCustodianPayload) => {
		const { data } = await useAPI<IRequestResponse<CustodianAutoPurchaseResponse>>(
			'/purchases/auto/add-custodian',
			{
				method: 'POST',
				body: payload
			}
		)

		return data.value
	}
	const addHealthCustodian = async (payload: AddHealthCustodianPayload) => {
		const { data } = await useAPI<IRequestResponse<HealthCustodianResponse[]>>('/purchases/health/add-custodian', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const addTravelCustodian = async (payload: AddTravelCustodianPayload) => {
		const { data } = await useAPI<IRequestResponse<CustodianTravelPurchaseResponse['travel_details']>>(
			'/purchases/travel/add-custodian',
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}
	const addGadgetCustodian = async (payload: AddGadgetCustodianPayload) => {
		const { data } = await useAPI<IRequestResponse<GadgetCustodianPurchaseResponse['gadget_details']>>(
			'/purchases/gadget/add-custodian',
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}

	const getPendingPolicies = async (query: { purchase_id?: string; [id: string]: unknown } = { purchase_id: '' }) => {
		const { validation, ...rest } = query
		const { data } = await useAPI<IRequestResponse<PaginationContext<PolicyResponse[]>>>('/pending-policies', {
			query: rest
		})

		return data.value?.data ?? null
	}

	const getPendingPolicy = async <T = PaginationContext<PolicyResponse[]>>(payload: {
		param?: string
		query?: object
	}) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/pending-policies/${payload?.param ?? ''}`, {
			query: payload.query
		})
		return data.value?.data ?? null
	}
	const addCustodianWithoutSlot = async (payload: AddHealthCustodianPayload) => {
		const { data } = await useAPI<IRequestResponse<ComputeStaffWithNoSlotResponse>>(
			'/purchases/health/add-custodian-no-slot',
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}
	const computeCustodianWithoutSlot = async (payload: AddHealthCustodianPayload) => {
		const { data } = await useAPI<IRequestResponse<ComputeStaffWithNoSlotResponse>>(
			'/purchases/health/compute-add-custodian-no-slot',
			{
				method: 'PUT',
				body: payload
			}
		)
		return data.value
	}

	// Activation of Policy
	async function ActivatePolicy<T = AutoPolicyActivationPayload, R = unknown>(link: string, payload: T) {
		const { data } = await useAPI<IRequestResponse<R>>(`/activate/${link.replace('cover', 'auto')}`, {
			method: 'POST',
			body: payload as Record<string, unknown>
		})
		if (data?.value?.data) {
			// Update the todos
			const { getTodoList } = useNotificationApi()
			getTodoList()
		}
		return data
	}

	return {
		getCustodianAutoPurchase,
		addAutoCustodian,
		addTravelCustodian,
		getTravelCustodianPurchase,
		getCustodianGadgetPurchase,
		addGadgetCustodian,
		ActivatePolicy,
		addHealthCustodian,
		addCustodianWithoutSlot,
		computeCustodianWithoutSlot,
		getPendingPolicy,
		getPendingPolicies
	}
}
